/**
 * The form request page (`/form-request/:requestToken`, web-account-pages D6;
 * forms-web.html moment 2 A, centered): a form the studio sent, opened by its
 * link alone, with its kicker, name, type line and description, the fields, and
 * Submit; the answers are kept as the client types. Done is "All set" with
 * Back to Home.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'form-request',
  description: 'A form the studio sent: its name and description, the fields, Submit, and the screen after it. The answers are kept as the client types.',
  options: {},
  parts: {
    page: { description: 'The whole page.', required: false },
    back: { description: 'Back to home.', required: false },
    kicker: { description: 'Form request.', required: false },
    title: { description: "The form's name.", required: true, role: 'title' },
    kind: { description: "The form's kind: Intake form · Requested by your studio.", required: false },
    description: { description: "The form's description, when the studio wrote one.", required: false },
    anonymous: { description: 'The note that the answers are not linked to the client, on an anonymous form.', required: true },
    fields: { description: "The form's fields.", required: true },
    submit: { description: 'Submit.', required: true, states: ['disabled'], role: 'primary-action' },
    submitError: { description: 'The line above Submit when the answers could not be sent; the form stays, with them.', required: false },
    end: { description: 'The screen after Submit, or when the form can no longer be filled in.', required: true },
    endTitle: { description: 'All set, Already submitted, This form is no longer available, or Something went wrong.', required: true, role: 'title' },
    endText: { description: 'The line under the end title.', required: true },
    retry: { description: 'Try again, after the form could not be read.', required: false, role: 'secondary-action' },
    home: { description: 'Back to Home.', required: false, role: 'primary-action' },
  },
  actions: {
    submit: { description: "Sends the client's answers.", args: [{ name: 'responses', kind: 'answers' }] },
    retry: { description: 'Reads the form again.', args: [] },
    goHome: { description: 'Opens the home page.', args: [] },
  },
  events: {
    submitted: 'The client sent the form.',
  },
});

/** What the form request page knows. */
export interface FormRequestState {
  /** loading until the form is read; form while it is filled in; the rest are end screens. */
  readonly status: 'loading' | 'form' | 'submitting' | 'success' | 'consumed' | 'revoked' | 'unavailable' | 'error';
  /** The form's name, once read. */
  readonly title: string | null;
  /** The form's kind line: Intake form · Requested by your studio. */
  readonly kind: string | null;
  /** True when the answers are not linked to the client. */
  readonly anonymous: boolean;
  /** The last submit failed without ending the request: the form stays with its answers (Codex review 2026-10-26). */
  readonly submitFailed: boolean;
}

/** What a drawing can ask the form request page to do. */
export interface FormRequestActions {
  /** Send the answers (the fields' submit calls it with their responses). */
  readonly submit: (responses: readonly { readonly fieldId: string; readonly value: unknown }[]) => void;
  /** Read the form again after it could not be read. */
  readonly retry: () => void;
  /** Open the home page. */
  readonly goHome: () => void;
}

/** What the form request page tells its host. */
export interface FormRequestEvents {
  /** The client sent the form. */
  readonly submitted: { readonly formId: string };
}
